import { Stack } from '@mui/material';
import { GridRowParams } from '@mui/x-data-grid-pro';
import { useCallback } from 'react';

import { SmallTableEmptyState } from '@/components/display/SmallTableEmptyState/SmallTableEmptyState';
import { Button } from '@/components/form/baseInputs/Button';
import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { User01Icon } from '@/components/icons/User01Icon';
import { UserPlus02Icon } from '@/components/icons/UserPlus02Icon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { TableEmptyState } from '@/components/tables/DataTable/components/TableEmptyState';
import { MAXIMUM_ROW_COUNT } from '@/components/tables/DataTable/constants';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { COLORS } from '@/styles/tokens/colors';

import {
  getClientPeopleListViewTableColumns,
  PeopleListSuggestion,
  ROW_TYPE_INDIVIDUAL,
  ROW_TYPE_ORGANIZATION,
  RowType,
} from './ClientPeopleListView.utils';

function ClientPeopleListViewTableEmptyState({
  openClientProfileModal,
}: {
  openClientProfileModal: (clientProfileId: string | null) => void;
}) {
  const canEdit = useCanEditHouseholdData();

  return (
    <TableEmptyState text="No people or organizations">
      <Button
        size="sm"
        variant="primary"
        disabled={!canEdit}
        onClick={() => openClientProfileModal(null)}
        startIcon={UserPlus02Icon}
      >
        Add person
      </Button>
    </TableEmptyState>
  );
}

export interface ClientDetailsPeopleOrganizationsTableProps {
  listContent: RowType[];
  loading?: boolean;
  openClientProfileModal: (clientProfileId: string | null) => void;
  openClientOrganizationModal: (clientOrganizationId: string | null) => void;
  onLocateSuggestionClick?: (suggestion: PeopleListSuggestion) => void;
}

export function ClientDetailsPeopleOrganizationsTable({
  listContent,
  loading,
  openClientProfileModal,
  openClientOrganizationModal,
  onLocateSuggestionClick,
}: ClientDetailsPeopleOrganizationsTableProps) {
  const canEdit = useCanEditHouseholdData();

  const handleEditClick = useCallback(
    (row: RowType) => {
      if (row.kind === ROW_TYPE_INDIVIDUAL) {
        openClientProfileModal(row.id);
      } else if (row.kind === ROW_TYPE_ORGANIZATION) {
        openClientOrganizationModal(row.id);
      }
    },
    [openClientProfileModal, openClientOrganizationModal]
  );

  // Guard on loading so the empty state doesn't flash before the initial
  // query resolves (rows are empty while the request is in flight).
  if (listContent.length === 0 && !loading) {
    return (
      <SmallTableEmptyState
        fill="flex"
        icon={<User01Icon size={32} color={COLORS.TEXT.PRIMARY} />}
        heading="No people or organizations"
        description="Add people and organizations to track relationships and roles"
        action={
          canEdit ? (
            <ButtonWithPopover
              label="Add"
              variant="primary"
              size="sm"
              startIcon={PlusIcon}
              popperVariant="menuBelow"
            >
              <MenuItem
                label="New individual"
                onClick={() => openClientProfileModal(null)}
              />
              <MenuItem
                label="New organization"
                onClick={() => openClientOrganizationModal(null)}
              />
            </ButtonWithPopover>
          ) : undefined
        }
      />
    );
  }

  return (
    <Stack direction="column">
      <DataTable
        columns={getClientPeopleListViewTableColumns(
          canEdit,
          onLocateSuggestionClick,
          handleEditClick
        )}
        rows={listContent}
        loading={loading}
        pageSize={MAXIMUM_ROW_COUNT}
        hideFooterPagination
        pagination={false}
        slots={{
          noRowsOverlay: () => (
            <ClientPeopleListViewTableEmptyState
              openClientProfileModal={openClientProfileModal}
            />
          ),
        }}
        onRowClick={(params: GridRowParams<RowType>) => {
          const { row } = params;
          if (row.kind === ROW_TYPE_INDIVIDUAL) {
            openClientProfileModal(row.id);
          } else if (row.kind === ROW_TYPE_ORGANIZATION) {
            openClientOrganizationModal(row.id);
          }
        }}
      />
    </Stack>
  );
}
